/* eslint-disable no-nested-ternary */
import { useAuth } from 'hooks/AuthContext'
import { getPathFrom, removePahtFrom } from 'hooks/usePathsRedirect'
import { setUser } from 'hooks/useSessionUser'
import { User } from 'hooks/useSignedUser/types'
import { useTokenApi } from 'hooks/useTokenApi'
import { SelectOption } from 'new-components/MultiSelect'
import { RegistrationContextValue } from 'pages/Registration/hooks/useRegistration'
import { requiredFormSchema } from 'pages/Registration/schemas/registrationForms'
import { FormEvent, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { toast } from 'react-toastify'
import api, { defaultsHeadersAxios } from 'services/api'
import { getAccessToken } from '../../../../../services/authToken'
import { clearMask } from 'utils/formatters'
import { useScheduling } from 'hooks/useScheduling'
import { usePrepareCart } from 'hooks/usePrepareCart'
import { clearSessionCartId, getSessionCartId, markSessionCartResolved } from 'utils/anonymousCart'
import { getFieldsWithErrorsMessage } from '../helpers'

export interface RegisterSuccess {
  success: boolean
  token: string
  user: User
  order_id?: number
  skipped_items?: { product_id: number; name: string }[]
}
export interface RegisterError {
  error: string
  success: boolean
}

interface FetchSpecialtiesResponse {
  expertises: {
    id: number
    name: string
    rqe: string
  }[]
}

const useRegistrationForm = (formFields: RegistrationContextValue) => {
  const history = useHistory()
  const { state } = useLocation() as any
  const { setTokenApi } = useTokenApi()
  const { getRoles } = useAuth()
  const { isSchedulingFlow, onRegisterSuccess } = useScheduling()
  const { setOrderId } = usePrepareCart()

  const {
    preferredName,
    typeOfBoard,
    boardNumber,
    boardFu,
    typeOfBoardOther,
    path,
    specialties,
    specialtiesRQE,
    birthdate,
    rg,
    rne,
    genderIdentity,
    name,
    nationality,
    cpf,
    cellphone,
    email,
    emailConfirmation,
    password,
    passwordConfirmation,
    gender,
    hasPrescribedBefore,
    telephone,

    errors,
    setErrors,
    setLoading,
    removeFieldFromErrors
  } = formFields
  const [agreedToUseTerms, setAgreedToUseTerms] = useState(false)
  const [receivePromotionalEmails, setReceivePromotionalEmails] = useState(false)
  const [specialtiesList, setSpecialtiesList] = useState<SelectOption[]>([])

  useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        const response = await api.get<FetchSpecialtiesResponse>('/expertises/all')
        setSpecialtiesList(
          response.data.expertises.map((expertise) => ({
            value: String(expertise.id),
            label: expertise.name,
            rqe: expertise.rqe
          }))
        )
      } catch (err) {
        console.error('Houve algum erro ao buscar as especialidades. Tente novamente mais tarde.')
      }
    }
    fetchSpecialties()
  }, [])

  const onRegistrationFormSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const normalizedGender = gender === 'prefer-not' ? null : gender
    const requiredFieldsValidation = await requiredFormSchema.safeParseAsync({
      typeOfBoard,
      path,
      birthdate,
      rg,
      rne,
      genderIdentity,
      name,
      nationality,
      cpf,
      cellphone,
      email,
      emailConfirmation,
      password,
      passwordConfirmation,
      gender,
      agreedToUseTerms
    })

    if (!requiredFieldsValidation.success) {
      const newErrors = requiredFieldsValidation.error.errors
      setErrors(newErrors)
      toast.dismiss()
      toast.error(getFieldsWithErrorsMessage(newErrors), {
        autoClose: false
      })
      return
    }
    setErrors(undefined)

    try {
      setLoading(true)
      const shouldForceGeneralist = path === 'professional' && typeOfBoard === 'CRM'
      const ensuredSpecialties = shouldForceGeneralist && specialties.length === 0 ? ['160'] : specialties
      const sessionCartId = getSessionCartId()
      const {
        data: { token, user, order_id }
      } = await api.post<RegisterSuccess>('/register', {
        name,
        preferred_name: preferredName,
        birthday: birthdate,
        email,
        cpf: clearMask(cpf),
        rg,
        rne,
        gender_identity: genderIdentity,
        gender: normalizedGender,
        nationality,
        password,
        phone: clearMask(telephone),
        council: typeOfBoard !== 'other' ? typeOfBoard : typeOfBoardOther,
        nr_council: boardNumber,
        uf_council: boardFu,
        nr_rqe: specialtiesRQE.length > 0 && specialtiesRQE[0] ? specialtiesRQE[0] : null,
        whatsapp: clearMask(cellphone),
        expertises: ensuredSpecialties.map((specialty) => ({ id: Number(specialty) })),
        already_prescribed: hasPrescribedBefore || 4,
        newsletter: receivePromotionalEmails,
        registration_type: path === 'patient' ? 1 : 2,
        prescriber: path === 'patient' ? 0 : 1,
        ...(sessionCartId ? { session_cart_id: sessionCartId } : {})
      })

      if (sessionCartId) {
        markSessionCartResolved(sessionCartId)
      }

      setTokenApi(token)
      defaultsHeadersAxios(token)
      await setUser(user)
      getRoles()
      if (isSchedulingFlow && onRegisterSuccess) {
        onRegisterSuccess()
        return
      }
      if (order_id) {
        setOrderId(order_id)
        clearSessionCartId()
        history.push('/carrinho')
      } else {
        const pathFrom = await getPathFrom()
        const pageToRedirect = pathFrom || '/painel-do-usuario'
        history.push(pageToRedirect)
      }

      const isHealthProfessional = user.registration_type === 2
      if (isHealthProfessional) {
        let healthProfessionalToken: string | undefined
        try {
          const cannectToken = getAccessToken()
          if (cannectToken) {
            const { data } = await api.post(
              '/generate-assistant-token',
              {},
              {
                headers: {
                  Authorization: `Bearer ${cannectToken}`
                }
              }
            )
            healthProfessionalToken = (data as any)?.token
          }
        } catch (err) {
          console.error('Erro ao gerar token para profissionais da saúde:', err)
        }

        const healthProfessionalBaseUrl =
          import.meta.env.VITE_APP_HEALTH_PROFESSIONAL_BASE_URL || 'http://localhost:3003/'

        if (healthProfessionalToken) {
          window.open(`${healthProfessionalBaseUrl}login?token=${healthProfessionalToken}`)
        } else {
          window.open(`${healthProfessionalBaseUrl}login`)
        }
      }

      await removePahtFrom()

      toast.success('Cadastro realizado com sucesso!')
    } catch (err: any) {
      const error = err?.response?.data as RegisterError

      toast.error(error?.error ? error?.error : 'Houve um erro ao realizar o cadastro.')
    } finally {
      setLoading(false)
    }
  }

  const changeAgreedToUseTerms = () => {
    setAgreedToUseTerms((state) => !state)
    if (errors) {
      removeFieldFromErrors('agreedToUseTerms')
    }
  }

  return {
    onRegistrationFormSubmit,
    changeAgreedToUseTerms,
    agreedToUseTerms,
    setAgreedToUseTerms,
    receivePromotionalEmails,
    setReceivePromotionalEmails,
    specialtiesList
  }
}

export default useRegistrationForm
